import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import RemoveIcon from "@mui/icons-material/Remove";
import { Skeleton } from "@mui/material";

import {
  AggregateMetricsRequest,
  NUMERIC_AGGREGATIONS,
  NumericAggregation,
  Period,
} from "@/shared/domain/metrics";
import { useAggregateMetrics } from "@/shared/domain/metrics/hooks";

import { computeTrend, TrendDirection } from "./computeTrend";
import { TileConfig } from "./dashboardParams";
import { formatPercent } from "./formatPercent";
import { FuzzySelect } from "./FuzzySelect";
import styles from "./TrendTile.module.css";

const PERIOD_NOUN: Record<Period, string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};

const DIRECTION_CLASS: Record<TrendDirection, string> = {
  up: styles.up,
  down: styles.down,
  flat: styles.flat,
};

const DIRECTION_ICON: Record<TrendDirection, typeof RemoveIcon> = {
  up: ArrowUpwardIcon,
  down: ArrowDownwardIcon,
  flat: RemoveIcon,
};

export interface TrendTileProps {
  /** Metric this tile shows (controlled by the parent / URL). */
  name: string;
  aggregation: NumericAggregation;
  /** All selectable metric names (for the per-tile metric picker). */
  allNames: string[];
  /** Fired when the user changes this tile's metric or aggregation. */
  onChange: (next: TileConfig) => void;
  orgId: string | undefined;
  period: Period;
  startTimeNs: number;
  endTimeNs: number;
  deviceIds?: string[];
  sessionIds?: string[];
}

/**
 * A single KPI tile: the current-period aggregate value for one metric plus its
 * change vs the previous period. Fully controlled — its metric + aggregation
 * selection is owned by the parent (persisted in the URL).
 */
export function TrendTile({
  name,
  aggregation,
  allNames,
  onChange,
  orgId,
  period,
  startTimeNs,
  endTimeNs,
  deviceIds,
  sessionIds,
}: TrendTileProps) {
  const request: AggregateMetricsRequest = {
    name,
    period,
    aggregation,
    time_filter: "end_time",
    start_time_ns: startTimeNs,
    end_time_ns: endTimeNs,
    include_device_ids: deviceIds,
    include_session_ids: sessionIds,
  };

  const { data, isLoading } = useAggregateMetrics(request, orgId);
  const trend = computeTrend(data?.records);

  const directionClass = DIRECTION_CLASS[trend.direction];
  const DirectionIcon = DIRECTION_ICON[trend.direction];

  const valueText =
    trend.current === null
      ? "—"
      : trend.current.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className={styles.tile}>
      <div className={styles.pickers}>
        <FuzzySelect
          className={styles.metricPicker}
          label="Metric"
          value={name}
          options={allNames}
          onChange={(next) => onChange({ name: next, aggregation })}
        />
        <FuzzySelect
          className={styles.aggPicker}
          label="Aggregation"
          value={aggregation}
          options={[...NUMERIC_AGGREGATIONS]}
          onChange={(next) =>
            onChange({ name, aggregation: next as NumericAggregation })
          }
        />
      </div>

      {isLoading ? (
        <Skeleton variant="text" width={80} height={36} />
      ) : (
        <div className={styles.value}>{valueText}</div>
      )}

      <div className={`${styles.trend} ${directionClass}`}>
        {trend.previous === null ? (
          <span className={styles.changeLabel}>
            No prior {PERIOD_NOUN[period]}
          </span>
        ) : (
          <>
            <DirectionIcon fontSize="inherit" />
            <span>{formatPercent(trend.percentChange)}</span>
            <span className={styles.changeLabel}>
              vs previous {PERIOD_NOUN[period]}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
